import { InputMaskProcessingBase } from '../input-mask-processing.base';

const GROUP_BY = 3;

const JS_DECIMAL_LIMIT = 6;

const NOT_NUMERIC_RX = /[^0-9]/g;
const ZEROS_RX = /0/g;

export class NumberMaskProcessing extends InputMaskProcessingBase {
  /**
   * Allowed number of decimal digits. A set `.fixedDecimals` will override this.
   * Override on the extending class for fixed value (defaults to
   * `field.config.validation.fixedDecials` > `field.config.validation.allowedDecials`)
   * Capped at JS decimal precision limit (6)
   */
  protected allowedDecimals = this.validations.allowedDecimals;

  /**
   * @inheritdoc
   */
  protected doubleProcess = true;

  /**
   * Force a fixed number of decimal digits (defaults to `field.config.validation.fixedDecials`)
   * Capped at JS decimal precision limit (6)
   */
  protected fixedDecimals = this.validations.fixedDecimals;

  /**
   * Optionally set a static `max` number value for the mask (otherwise it is read from `field.config.validation.max`)
   */
  protected max = Math.min(
    Number.MAX_SAFE_INTEGER,
    typeof this.validations.max === 'number' ? this.validations.max : Number.MAX_SAFE_INTEGER
  );

  /**
   * Optionally set a static `min` number value for the mask (otherwise it is read from `field.config.validation.min`)
   */
  protected min = Math.max(
    Number.MIN_SAFE_INTEGER,
    typeof this.validations.min === 'number' ? this.validations.min : Number.MIN_SAFE_INTEGER
  );

  /**
   * A string to start with (ex: currency symbol)
   */
  protected prefix = '';

  /**
   * Shorthand to check if number can be a decimal
   */
  protected get isDecimal(): boolean {
    return typeof this.allowedDecimals !== 'number' || this.allowedDecimals > 0;
  }

  /**
   * Shorthand to check if number can be signed
   */
  protected get isSigned(): boolean {
    return typeof this.min !== 'number' || this.min < 0;
  }

  /**
   * Provice length with grouping characters. Ex: ungrouped length for "1234567" is 7 => grouped will be "1,234,567", length: 9
   * @param ungroupedLen pure number string length
   */
  protected groupedLen(ungroupedLen: number): number {
    return ungroupedLen + (ungroupedLen - 1 - ((ungroupedLen - 1) % GROUP_BY)) / GROUP_BY;
  }

  /**
   * @inheritdoc
   */
  protected isCharAllowed(pos: number): boolean {
    const value = this.processed.value;
    const char = value[pos];
    if (char === '-') {
      // sign
      return pos === this.prefix.length && this.isSigned;
    }
    if (char === '.') {
      // decimal point
      return this.isDecimal && value.substr(0, pos).indexOf('.') < 0;
    }

    const dotPos = this.allowedDecimals > 0 ? value.indexOf('.') : -1;

    if (char === '0' && (pos < dotPos || dotPos === -1)) {
      // filter more than 1 zeros on the left
      const prev = value.substr(0, pos).replace(NOT_NUMERIC_RX, '');
      if (prev.length && !prev.replace(ZEROS_RX, '').length) {
        return false;
      }
    }

    if (dotPos > -1 && pos > dotPos + this.groupedLen(this.allowedDecimals)) {
      // out of allowed decimal count
      return false;
    }

    const pureIntLen = Math.max(String(this.min).length, String(this.max).length);
    if ((dotPos < 0 || pos < dotPos) && pos >= this.prefix.length + this.groupedLen(pureIntLen)) {
      // out of allowed int size
      return false;
    }

    return '0123456789'.indexOf(char) > -1; // non-zero number
  }

  /**
   * @inheritdoc
   */
  protected isGroupingChar(pos: number): string {
    const value = this.processed.value;
    const dotPos = value.indexOf('.');
    const signPos = value.indexOf('-');
    const signedPrefix = signPos > -1 && signPos < pos ? '-' + this.prefix : this.prefix;
    if (pos < signedPrefix.length) {
      return signedPrefix[pos];
    } else if (this.isDecimal && pos === signedPrefix.length && value[pos] === '.') {
      return '0'; // inject starting '0' for decimals
    } else if (dotPos === -1 || dotPos > pos) {
      // integer part
      if (pos > signedPrefix.length) {
        const numOnlyStr = (dotPos > -1 ? value.substr(0, dotPos) : value).replace(NOT_NUMERIC_RX, '');
        if (numOnlyStr.length > GROUP_BY && (pos - signedPrefix.length) % (GROUP_BY + 1) === (numOnlyStr.length % GROUP_BY || GROUP_BY)) {
          return ',';
        }
      }
    } else if (dotPos > -1 && pos - dotPos > 1 && (pos - dotPos - 1) % (GROUP_BY + 1) === GROUP_BY) {
      // decimal part
      return ',';
    }
  }

  /**
   * @inheritdoc
   */
  protected postProcessBlurred(): void {
    if (this.isDecimal) {
      // senitize trailing zeros and decimal points
      const processed = this.processed;
      let dotPos = processed.value.indexOf('.');

      if (typeof this.allowedDecimals === 'number' && this.allowedDecimals <= 0) {
        // integer case
        if (dotPos > -1) {
          processed.value = processed.value.substr(0, dotPos);
        }
      } else if (typeof this.fixedDecimals === 'number') {
        // fixed decimals
        if (processed.value) {
          if (dotPos < 0) {
            dotPos = processed.value.length;
            processed.value += '.';
          }
          while (processed.value.length <= dotPos + this.fixedDecimals) {
            processed.value += '0';
          }
        }
      } else if (dotPos > -1) {
        // non-fixed decimals with a decimal part
        while (processed.value.length >= dotPos && '0.'.indexOf(processed.value[processed.value.length - 1]) > -1) {
          processed.value = processed.value.substr(0, processed.value.length - 1); // remove trailing zeros and/or decimal point
        }
      }

      // fix selection max if characaters got removed
      processed.selection[0] = Math.min(processed.selection[0], processed.value.length);
      processed.selection[1] = Math.min(processed.selection[1], processed.value.length);
    }
  }

  /**
   * @inheritdoc
   */
  protected preProcess(): void {
    // cap+sanitize decimals
    if (typeof this.fixedDecimals === 'number') {
      this.allowedDecimals = this.fixedDecimals = Math.min(JS_DECIMAL_LIMIT, this.fixedDecimals);
    } else if (typeof this.allowedDecimals === 'number') {
      this.allowedDecimals = Math.min(JS_DECIMAL_LIMIT, this.allowedDecimals);
    }

    const pureIntLen = Math.max(String(this.min).length, String(this.max).length);
    this.maxLength =
      this.prefix.length +
      pureIntLen +
      this.groupedLen(pureIntLen) +
      (this.isDecimal ? this.allowedDecimals + 1 + this.groupedLen(this.allowedDecimals) : 0);
  }
}
